import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

export default function HelpConcierge() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || !user) return;
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: "Hi — this is the 24-hour concierge desk. What can I help with tonight?" }]);
    }
  }, [open, user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  if (!user) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    setTyping(true);
    // small realistic delay before typing indicator settles
    await new Promise((r) => setTimeout(r, 400));
    try {
      const { data } = await api.post("/help/chat", { message: text, conversation_id: conversationId });
      setConversationId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — the desk is briefly offline. One sec." }]);
    } finally {
      setTyping(false);
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="help-concierge-trigger"
        className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full bg-[#B91C2C] text-[#F5F5F7] flex items-center justify-center shadow-[0_10px_40px_-5px_rgba(255,51,85,0.5)] hover:bg-[#FF3355] transition-colors"
        aria-label="24-hour help"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-24 right-6 z-30 w-[360px] max-w-[92vw] h-[520px] max-h-[80vh] flex flex-col te-terminal-window"
            data-testid="help-concierge-panel"
          >
            <div className="border-b border-[#1F2833] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400 te-pulse-dot"></span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F5F5F7]">24-Hour Help</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70">online</span>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#B91C2C] text-[#F5F5F7] font-mono"
                        : "border border-[#1F2833] text-[#F5F5F7]"
                    }`}
                    data-testid={`concierge-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start">
                  <div className="border border-[#1F2833] px-3 py-2 text-sm text-[#C5C6C7] inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-[#FF3355] rounded-full animate-pulse"></span>
                    <span className="w-1.5 h-1.5 bg-[#FF3355] rounded-full animate-pulse" style={{ animationDelay: "0.15s" }}></span>
                    <span className="w-1.5 h-1.5 bg-[#FF3355] rounded-full animate-pulse" style={{ animationDelay: "0.3s" }}></span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form onSubmit={send} className="border-t border-[#1F2833] p-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                data-testid="concierge-input"
                className="flex-1 bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="concierge-send"
                className="bg-[#B91C2C] hover:bg-[#FF3355] text-[#F5F5F7] p-2 disabled:opacity-40"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
